import { useSyncExternalStore } from 'react';

/**
 * The reader's own clock, safely.
 *
 * A time printed in the reader's zone ("today, 6:40 PM") cannot be rendered on
 * the server, which does not know the zone. `useViewerClock` is false for the
 * server render AND for the client's hydration pass (React uses the server
 * snapshot for both), then true on the first commit after, so the markup the
 * browser first adopts is exactly the server's and only the text changes after.
 *
 * It re-reads the reader's calendar day when the tab regains focus or becomes
 * visible, so a page left open past midnight stops saying "today" for what is
 * now yesterday. The snapshot is the day string, which only changes then.
 */

let dayFormatter: Intl.DateTimeFormat | null | undefined;

/** The reader's local calendar day as YYYY-MM-DD; 'unknown' where Intl fails. */
const readDay = (): string => {
  if (dayFormatter === undefined) {
    try {
      dayFormatter = new Intl.DateTimeFormat('en-CA');
    } catch {
      dayFormatter = null;
    }
  }
  return dayFormatter ? dayFormatter.format(Date.now()) : 'unknown';
};

const readServerDay = (): string => '';

const subscribe = (onChange: () => void): (() => void) => {
  document.addEventListener('visibilitychange', onChange);
  window.addEventListener('focus', onChange);
  return () => {
    document.removeEventListener('visibilitychange', onChange);
    window.removeEventListener('focus', onChange);
  };
};

/** The reader's local day, or '' on the server and during hydration. */
export const useViewerDay = (): string => useSyncExternalStore(subscribe, readDay, readServerDay);

/** True once the browser can print times in the reader's own zone. */
export const useViewerClock = (): boolean => useViewerDay() !== '';
